import { formatDate } from "@certn/utils";

import { DocumentTypeOption, Employer, QualifyingEmployer } from "./types";

function formatEmployerDateRange(
  employer: Pick<Employer, "start_date" | "end_date">,
  presentLabel = "Present"
): string {
  const startDate = employer.start_date
    ? formatDate(employer.start_date, "MMM yyyy")
    : "";
  const endDate = employer.end_date
    ? formatDate(employer.end_date, "MMM yyyy")
    : presentLabel;
  const dateRange =
    startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate;

  return dateRange;
}

export function formatEmployerCardTitle(
  employer: Employer,
  presentLabel = "Present"
): string {
  const dateRange = formatEmployerDateRange(employer, presentLabel);

  return [employer.company_name, dateRange].filter(Boolean).join(": ");
}

export function findEmployerById(
  employers: Employer[] | undefined,
  employerId: string | undefined
): Employer {
  if (!employerId) {
    return {};
  }

  return (
    (employers || []).find((employer) => employer.employer_id === employerId) ??
    {}
  );
}

export function getQualifyingEmployers(
  employers: Employer[] | undefined,
  qualifyingEmployers: QualifyingEmployer[]
) {
  const employerList = employers || [];

  return qualifyingEmployers
    .map(({ employer_id: employerId, document_types = [] }) => {
      const employer = findEmployerById(employerList, employerId);
      if (!employer.employer_id) {
        return null;
      }

      return { employer, employerId, documentTypes: document_types };
    })
    .filter(
      (
        entry
      ): entry is {
        employer: Employer;
        employerId: string;
        documentTypes: DocumentTypeOption[];
      } => entry !== null
    );
}

export function getEmploymentDocumentTypeLabel(
  value: string,
  documentTypes: DocumentTypeOption[] = []
): string {
  return documentTypes.find((entry) => entry.const === value)?.title ?? value;
}

export function getDocumentTypesForEmployer(
  qualifyingEmployers: QualifyingEmployer[],
  employerId: string | undefined
): DocumentTypeOption[] {
  if (!employerId) {
    return [];
  }

  return (
    qualifyingEmployers.find((employer) => employer.employer_id === employerId)
      ?.document_types ?? []
  );
}

export function getDocumentTypeOption(
  documentTypes: DocumentTypeOption[],
  documentType: string | undefined
): DocumentTypeOption | undefined {
  if (!documentType) {
    return undefined;
  }

  return documentTypes.find((entry) => entry.const === documentType);
}
